import { useState, useEffect, useCallback, type ComponentType } from "react";
import "./index.css";
import logo from "./logo.png";

import HeroSlide from "./slides/HeroSlide";
import StampSlide from "./slides/StampSlide";
import ShiftSlide from "./slides/ShiftSlide";
import VisionSlide from "./slides/VisionSlide";
import DashboardSlide from "./slides/DashboardSlide";
import JarvisSlide from "./slides/JarvisSlide";
import ArchitectureSlide from "./slides/ArchitectureSlide";
import BuildersSlide from "./slides/BuildersSlide";
import CTASlide from "./slides/CTASlide";

const slideComponents: ComponentType<{ onNext: () => void; isActive: boolean }>[] = [
  HeroSlide,          // 1. Hero
  StampSlide,         // 2. "Next Robinhood" stamp
  ShiftSlide,         // 3. The Shift (Manual vs Autonomous)
  VisionSlide,        // 3. What is Avo?
  DashboardSlide,     // 4. From Marketplace to Autonomy (evolution → Jarvis)
  JarvisSlide,        // 5. Meet Jarvis
  ArchitectureSlide,  // 6. Architecture
  BuildersSlide,      // 7. For Builders
  CTASlide,           // 8. Start Allocating. Intelligently.
];

export function App() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const totalSlides = slideComponents.length;

  const nextSlide = useCallback(() => {
    if (currentSlide < totalSlides - 1 && !isAnimating) {
      setIsAnimating(true);
      setCurrentSlide((prev) => prev + 1);
      setTimeout(() => setIsAnimating(false), 800);
    }
  }, [currentSlide, isAnimating, totalSlides]);

  const prevSlide = useCallback(() => {
    if (currentSlide > 0 && !isAnimating) {
      setIsAnimating(true);
      setCurrentSlide((prev) => prev - 1);
      setTimeout(() => setIsAnimating(false), 800);
    }
  }, [currentSlide, isAnimating]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeydown = (e: KeyboardEvent) => {
      if (["ArrowDown", "ArrowRight", " "].includes(e.key)) {
        e.preventDefault();
        nextSlide();
      }
      if (["ArrowUp", "ArrowLeft"].includes(e.key)) {
        e.preventDefault();
        prevSlide();
      }
    };
    document.addEventListener("keydown", handleKeydown);
    return () => document.removeEventListener("keydown", handleKeydown);
  }, [nextSlide, prevSlide]);

  // Wheel navigation
  useEffect(() => {
    let wheelTimeout: ReturnType<typeof setTimeout>;
    const handleWheel = (e: WheelEvent) => {
      clearTimeout(wheelTimeout);
      wheelTimeout = setTimeout(() => {
        if (e.deltaY > 50) nextSlide();
        if (e.deltaY < -50) prevSlide();
      }, 50);
    };
    document.addEventListener("wheel", handleWheel, { passive: true });
    return () => document.removeEventListener("wheel", handleWheel);
  }, [nextSlide, prevSlide]);

  // Touch navigation
  useEffect(() => {
    let touchStartY = 0;
    const handleTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0]?.clientY ?? 0;
    };
    const handleTouchEnd = (e: TouchEvent) => {
      const touchEndY = e.changedTouches[0]?.clientY ?? 0;
      if (touchStartY - touchEndY > 50) nextSlide();
      if (touchEndY - touchStartY > 50) prevSlide();
    };
    document.addEventListener("touchstart", handleTouchStart);
    document.addEventListener("touchend", handleTouchEnd);
    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [nextSlide, prevSlide]);

  const progress = ((currentSlide + 1) / totalSlides) * 100;

  const getSlideClassName = (index: number) => {
    if (index === currentSlide) return "slide active";
    if (index < currentSlide) return "slide prev";
    return "slide";
  };

  return (
    <>
      {/* Logo - top right corner */}
      <div className="fixed top-6 right-8 z-50 flex items-center gap-2">
        <img src={logo} alt="Avo" className="w-8 h-8 rounded-lg" />
      </div>

      {/* Progress bar */}
      <div className="fixed top-0 left-0 w-full h-1 bg-gray-900 z-50">
        <div
          className="h-full bg-gradient-to-r from-avo-cyan via-avo-purple to-avo-lime transition-all duration-500 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Scroll indicator */}
      <div className="fixed right-8 bottom-8 flex-col gap-4 z-50 hidden md:flex">
        <div className="text-xs font-mono text-gray-500 mb-2 rotate-90 origin-right translate-x-2">
          SCROLL
        </div>
        <div className="w-[1px] h-12 bg-gray-800 self-center">
          <div className="w-full h-1/2 bg-avo-lime animate-pulse" />
        </div>
      </div>

      {/* Slides */}
      <div className="slide-container" id="deck">
        {slideComponents.map((SlideComponent, index) => (
          <section key={index} className={getSlideClassName(index)}>
            <SlideComponent onNext={nextSlide} isActive={index === currentSlide} />
          </section>
        ))}
      </div>

      {/* Navigation buttons */}
      <div className="fixed bottom-8 left-8 flex gap-2 z-50">
        <button
          onClick={prevSlide}
          className="w-12 h-12 rounded-full border border-gray-800 bg-black/50 hover:bg-white/10 hover:border-gray-600 flex items-center justify-center transition-all backdrop-blur-md cursor-pointer"
        >
          ↑
        </button>
        <button
          onClick={nextSlide}
          className="w-12 h-12 rounded-full border border-gray-800 bg-black/50 hover:bg-white/10 hover:border-gray-600 flex items-center justify-center transition-all backdrop-blur-md cursor-pointer"
        >
          ↓
        </button>
      </div>
    </>
  );
}

export default App;
